import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import HomeView from "./view";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../../redux/reducers/Models";
import {
  debtsPulling,
  requestDebts,
  setUser,
  clearUser,
  authorize,
} from "../../redux/ducks/user";
import { clearCustomer, setCustomer } from "../../redux/ducks/customer";
import { PropsView } from "./Models";
import {
  Debts,
  FormattedDebts,
  buildDebtsCart,
  isDebtAvailableForPayment,
} from "@/utils/transform";
import { resolveDebtsPullingResult } from "@/utils/resolveDebtsPullingResult";
import { getIsValidString } from "@/utils/functions";
import { maskEmail, maskPhone } from "@/utils/maskContact";
import useRedirect from "../../hooks/useRedirect";
import { useNavigate, useLocation } from "react-router-dom";
import { useSingleFlag } from "@/hooks/useFlags";
import useCaptcha from "@/hooks/useCaptcha";
import useCaptchaEnterprise from "@/hooks/useCaptchaEnterprise";
import { clearThemeFromStorage } from "@/contexts/ThemeContext";
import { useIsWebView } from "@/hooks/useIsWebView";
import { useScrapersConsultation } from "@/hooks/useScrapersConsultation";
import { resetScrapersSimulation } from "@/domain/scrapers/fetchScrapersConsultation";
import { useQueryClient } from "@tanstack/react-query";
import { resolveSourceChannel } from "@/hooks/useTheme";
import { resolveHomeDebtsBootstrap } from "./resolveHomeDebtsBootstrap";
import {
  AvulsoRetentionAction,
  resolveAvulsoRetentionStep,
} from "./resolveAvulsoRetentionStep";
import { resolveHomeAvulsoBackdrop } from "./resolveHomeAvulsoBackdrop";
import { shouldStartScrapersConsultation } from "./shouldStartScrapersConsultation";
import { trackViewItemList } from "@/analytics/ecommerce";
import useTrackOnce from "@/analytics/ecommerce/useTrackOnce";

export default function Home() {
  useRedirect("home");

  const navigate = useNavigate();
  const location = useLocation();
  const skipBackdrops = !!(location.state as any)?.skipBackdrops;
  const activePollingIdRef = useRef<string | null>(null);
  const hasMountedRef = useRef(false);
  const hasStartedDebtsSearchRef = useRef(false);
  const avulsoHandledRef = useRef(false);
  const noDataHandledRef = useRef(false);
  const isLoadingRef = useRef(false);
  const debtsRef = useRef<FormattedDebts[]>([]);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isWebView = useIsWebView();
  const user = useSelector(
    (
      state: ReducersTypes & {
        userReducer: ReducersTypes["userReducer"] & {
          cart: PropsView["selectables"];
          debts: PropsView["items"];
        };
      },
    ) => state.userReducer,
  );

  const { isEnabled: isCaptchaMtcCaptcha, isLoading: isLoadingCaptchaFlag } =
    useSingleFlag("PD_CAPTCHA_MTCAPTCHA");
  const { isEnabled: showAvulsoWhatsApp } = useSingleFlag(
    "PD_PIN_AVULSO_WHATSAPP",
  );
  const { isEnabled: showAvulsoEmail } = useSingleFlag("PD_PIN_AVULSO_EMAIL");
  const [isLoading, setIsLoading] = useState(true);
  isLoadingRef.current = isLoading;
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);

  const captchaName = useMemo(
    () => `mtcaptcha-home-modal-${captchaKey}`,
    [captchaKey],
  );
  const recaptchaName = useMemo(
    () => `home-modal-captcha-${captchaKey}`,
    [captchaKey],
  );

  const setIsCaptchaVerifiedCallback = useCallback((value: boolean) => {
    setIsCaptchaVerified(value);
  }, []);

  useCaptcha({
    setIsVerified: isCaptchaMtcCaptcha
      ? setIsCaptchaVerifiedCallback
      : () => {},
    captchaName,
  });

  useCaptchaEnterprise({
    setIsVerified: !isCaptchaMtcCaptcha
      ? setIsCaptchaVerifiedCallback
      : () => {},
    captchaName: recaptchaName,
    action: "search",
  });

  const goBack = useCallback(() => navigate(-1), [navigate]);
  const goGetout = useCallback(() => {
    clearThemeFromStorage();

    dispatch(clearCustomer());
    dispatch(clearUser());

    // Tentar sair da webview se estiver em uma
    try {
      // React Native WebView
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({ type: "close" }),
        );
        return;
      }

      if ((window as any).webkit?.messageHandlers?.close) {
        (window as any).webkit.messageHandlers.close.postMessage({});
        return;
      }

      if (
        (window as any).Android?.finish &&
        typeof (window as any).Android.finish === "function"
      ) {
        (window as any).Android.finish();
        return;
      }

      // Fallback: tentar window.close() se a janela foi aberta por script
      if (window.opener) {
        window.close();
        return;
      }
    } catch (error) {
      console.warn("Erro ao tentar fechar webview:", error);
    }

    navigate("/");
  }, [dispatch, navigate]);
  const goToVehicles = useCallback(() => navigate("/vehicles"), [navigate]);
  const goToProfile = useCallback(() => navigate("/profile"), [navigate]);

  const resetCaptcha = useCallback(() => {
    setIsCaptchaVerified(false);
    if (isCaptchaMtcCaptcha) {
      if ((window as any)?.mtcaptcha?.resetUI) {
        (window as any).mtcaptcha.resetUI(captchaName);
      }
    } else {
      if ((window as any).grecaptcha?.enterprise?.reset) {
        (window as any).grecaptcha.enterprise.reset();
      } else if ((window as any).grecaptcha?.reset) {
        (window as any).grecaptcha.reset();
      }
      (window as any).recaptchaToken = null;
    }
  }, [isCaptchaMtcCaptcha, captchaName]);

  const incrementCaptchaKey = useCallback(() => {
    setCaptchaKey((prev) => prev + 1);
  }, []);

  const startPulling = useCallback(
    (reqId: string) => {
      if (!getIsValidString(reqId)) {
        return;
      }

      activePollingIdRef.current = reqId;
      setIsLoading(true);

      const handleDebtsPullingCallback = (
        success: boolean,
        code?: string | Debts[],
        partialPassages?: Debts[],
      ) => {
        if (activePollingIdRef.current !== reqId) return;

        const result = resolveDebtsPullingResult({
          success,
          code,
          partialPassages,
          existingDebts: debtsRef.current,
        });

        if (result.kind === "partial") {
          debtsRef.current = result.debts;
          dispatch(
            setUser({
              debts: result.debts,
              cart: buildDebtsCart(result.debts),
            }),
          );
          return;
        }

        if (result.kind === "retry") {
          return;
        }

        if (result.kind === "fail") {
          debtsRef.current = [];
          setIsLoading(false);
          dispatch(
            setUser({
              debts: [],
              cart: undefined,
            }),
          );
          return;
        }

        debtsRef.current = result.debts;
        dispatch(
          setUser({
            debts: result.debts,
            cart: buildDebtsCart(result.debts),
          }),
        );
        setIsLoading(false);
      };

      dispatch(
        debtsPulling({
          requestId: reqId,
          callback: handleDebtsPullingCallback,
        }),
      );
    },
    [dispatch],
  );

  const onSearchNewPlate = useCallback(
    async (newPlate: string) => {
      const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");

      if (!cleanPlate || cleanPlate.length < 5) {
        return;
      }

      activePollingIdRef.current = null;
      hasStartedDebtsSearchRef.current = true;
      avulsoHandledRef.current = !!skipBackdrops;
      noDataHandledRef.current = !!skipBackdrops;
      setIsLoading(true);
      resetScrapersSimulation(cleanPlate);
      queryClient.removeQueries({ queryKey: ["scrapers-consultation"] });

      dispatch(
        setUser({
          plate: cleanPlate,
          requestId: undefined,
          debts: [],
          cart: undefined,
        }),
      );

      dispatch(
        setCustomer({
          plate: cleanPlate,
        }),
      );

      try {
        let captchaToken = "";
        let recaptchaAction: string | undefined;

        if (isCaptchaMtcCaptcha) {
          const mtcaptchaToken = (window as any)?.mtcaptcha?.getVerifiedToken?.(
            captchaName,
          );
          const mtcaptchaStatus = (window as any)?.mtcaptcha?.getStatus?.(
            captchaName,
          );

          if (mtcaptchaToken && mtcaptchaStatus?.isVerified) {
            captchaToken = mtcaptchaToken;
          } else {
            if ((window as any)?.mtcaptcha?.getVerifiedToken) {
              const anyToken = (window as any).mtcaptcha.getVerifiedToken(
                captchaName,
              );
              if (anyToken) {
                captchaToken = anyToken;
              }
            }

            if (!captchaToken) {
              captchaToken = (window as any)?.recaptchaToken || "";
            }
          }
        } else {
          let recaptchaToken = (window as any)?.recaptchaToken;

          if (!recaptchaToken && (window as any).executeRecaptcha) {
            (window as any).executeRecaptcha();
            await new Promise((resolve) => setTimeout(resolve, 300));
            recaptchaToken = (window as any)?.recaptchaToken;
          }

          if (recaptchaToken) {
            captchaToken = recaptchaToken;
          }

          recaptchaAction = (window as any).getRecaptchaAction
            ? (window as any).getRecaptchaAction()
            : undefined;
        }

        if (!captchaToken) {
          await new Promise((resolve) => setTimeout(resolve, 200));

          if (isCaptchaMtcCaptcha) {
            captchaToken =
              (window as any)?.mtcaptcha?.getVerifiedToken?.(captchaName) || "";
          } else {
            captchaToken = (window as any)?.recaptchaToken || "";
          }
        }

        dispatch(
          authorize({
            plate: cleanPlate,
            token: captchaToken,
            callback: (success: boolean) => {
              if (!success) {
                setIsLoading(false);
                dispatch(
                  setUser({
                    debts: [],
                    cart: undefined,
                  }),
                );
                return;
              }

              setTimeout(() => {
                dispatch(
                  requestDebts({
                    plate: cleanPlate,
                    callback: (success: boolean, code?: string) => {
                      if (!success) {
                        setIsLoading(false);
                        dispatch(
                          setUser({
                            debts: [],
                            cart: undefined,
                          }),
                        );
                        return;
                      }

                      if (getIsValidString(code as string)) {
                        dispatch(
                          setUser({
                            requestId: code,
                          }),
                        );
                      }
                    },
                  }),
                );
              }, 100);
            },
            recaptchaAction,
          }),
        );
      } catch (error) {
        setIsLoading(false);
      }
    },
    [dispatch, isCaptchaMtcCaptcha, captchaName, queryClient, skipBackdrops],
  );

  const onChange: PropsView["onChange"] = useCallback(
    (e) => {
      const { name, checked } = e.target;

      if (name === "all") {
        dispatch(
          setUser({
            cart: checked ? buildDebtsCart(user?.debts ?? []) : null,
          }),
        );
        return;
      }

      const debt = user?.debts?.find((item) => item.id === name);
      if (debt && !isDebtAvailableForPayment(debt)) {
        return;
      }

      const updatedSelectables =
        user?.cart?.filter((item) => item.name !== name) || [];

      if (checked) {
        updatedSelectables.push({ name, checked });
      }

      dispatch(
        setUser({
          cart: updatedSelectables.length ? updatedSelectables : null,
        }),
      );
    },
    [dispatch, user?.debts, user?.cart],
  );

  const handleRequestDebts = useCallback(
    (success: boolean, code?: string) => {
      if (!success) {
        setIsLoading(false);
        dispatch(
          setUser({
            debts: [],
          }),
        );
        return;
      }

      dispatch(
        setUser({
          requestId: code,
        }),
      );
    },
    [dispatch],
  );

  const startFreshDebtsSearch = useCallback(() => {
    if (!getIsValidString(user?.plate as string)) {
      setIsLoading(false);
      return;
    }

    activePollingIdRef.current = null;
    hasStartedDebtsSearchRef.current = true;
    avulsoHandledRef.current = !!skipBackdrops;
    noDataHandledRef.current = !!skipBackdrops;
    setIsLoading(true);
    resetScrapersSimulation(user?.plate as string);
    queryClient.removeQueries({ queryKey: ["scrapers-consultation"] });
    dispatch(
      setUser({
        requestId: undefined,
        debts: [],
        cart: undefined,
      }),
    );
    dispatch(
      requestDebts({
        plate: user?.plate as string,
        callback: handleRequestDebts,
      }),
    );
  }, [dispatch, user?.plate, handleRequestDebts, queryClient, skipBackdrops]);

  useEffect(() => {
    if (
      resolveHomeDebtsBootstrap({
        requestId: user?.requestId as string,
        isInitialMount: false,
        isLoading: isLoadingRef.current,
      }) === "pull"
    ) {
      startPulling(user?.requestId as string);
    }
  }, [user?.requestId, startPulling]);

  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;

    const bootstrapAction = resolveHomeDebtsBootstrap({
      requestId: user?.requestId as string,
      isInitialMount: true,
      isLoading: false,
      hasDebts: (user?.debts?.length ?? 0) > 0,
    });

    if (bootstrapAction === "reuse") {
      hasStartedDebtsSearchRef.current = true;
      setIsLoading(false);
      return;
    }

    if (bootstrapAction === "pull") {
      hasStartedDebtsSearchRef.current = true;
      startPulling(user?.requestId as string);
      return;
    }

    startFreshDebtsSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debts = user?.debts || [];
  debtsRef.current = debts;
  useTrackOnce(
    () => trackViewItemList({ flowType: "pagamento_avulso" }),
    debts.length > 0,
  );
  const isDefaultFlow =
    resolveSourceChannel({ company: user?.company }) === "site";
  const shouldStartScrapers = shouldStartScrapersConsultation({
    isDefaultFlow,
  });
  const { consultation: scrapersConsultation } = useScrapersConsultation(
    user?.plate || "",
    { enabled: shouldStartScrapers },
  );
  /* Fluxo avulso: sem company (não veio de URL siga-facil/zapay) e sem dados pessoais ainda */
  const isAvulsoFlow = !user?.company;
  const [showAvulsoBackdrop, setShowAvulsoBackdrop] = useState(false);
  const [avulsoChannel, setAvulsoChannel] = useState<"whatsapp" | "email">(
    "whatsapp",
  );
  const [showNoDataBackdrop, setShowNoDataBackdrop] = useState(false);
  const [showTagBackdrop, setShowTagBackdrop] = useState(false);
  const [showBeforeLeaveBackdrop, setShowBeforeLeaveBackdrop] = useState(false);
  const initialLoadDoneRef = useRef(false);
  const isTagFromRetentionRef = useRef(false);

  useEffect(() => {
    const result = resolveHomeAvulsoBackdrop({
      skipBackdrops,
      hasFinishedInitialConsultation: initialLoadDoneRef.current,
      isLoading,
      hasStartedConsultation: hasStartedDebtsSearchRef.current,
      alreadyHandled: avulsoHandledRef.current,
      debtsCount: debts.length,
      isAvulsoFlow,
    });

    initialLoadDoneRef.current = result.hasFinishedInitialConsultation;
    avulsoHandledRef.current = result.alreadyHandled;
    if (result.showBackdrop) {
      setShowAvulsoBackdrop(true);
    }
  }, [isLoading, debts.length, isAvulsoFlow, skipBackdrops]);

  useEffect(() => {
    if (skipBackdrops) {
      noDataHandledRef.current = true;
      return;
    }
    if (!initialLoadDoneRef.current) return;
    if (noDataHandledRef.current) return;
    if (!isLoading && debts.length === 0 && isAvulsoFlow) {
      noDataHandledRef.current = true;
    }
  }, [isLoading, debts.length, isAvulsoFlow, skipBackdrops]);

  const avulsoTotalValue = debts
    .reduce((sum, item) => sum + (item.original?.vlTotalPagar || 0), 0)
    .toFixed(2)
    .replace(".", ",");
  useEffect(() => {
    if (showAvulsoWhatsApp) {
      setAvulsoChannel("whatsapp");
      return;
    }
    if (showAvulsoEmail) {
      setAvulsoChannel("email");
    }
  }, [showAvulsoWhatsApp, showAvulsoEmail]);

  const applyRetentionStep = useCallback(
    (action: AvulsoRetentionAction) => {
      const { sheet, exit } = resolveAvulsoRetentionStep(action);
      isTagFromRetentionRef.current = sheet === "tag";
      setShowAvulsoBackdrop(sheet === "validation");
      setShowBeforeLeaveBackdrop(sheet === "beforeLeave");
      setShowTagBackdrop(sheet === "tag");
      if (exit) goGetout();
    },
    [goGetout],
  );
  const goToAvulsoPin = useCallback(() => {
    setShowAvulsoBackdrop(false);
    setShowBeforeLeaveBackdrop(false);
    setShowNoDataBackdrop(false);
    setShowTagBackdrop(false);
    navigate(`/register/pin/${avulsoChannel}`);
  }, [navigate, avulsoChannel]);
  const handleNoDataRegister = useCallback(() => {
    setShowNoDataBackdrop(false);
    navigate("/register");
  }, [navigate]);
  const handleNoDataDismiss = useCallback(() => {
    setShowNoDataBackdrop(false);
    setShowTagBackdrop(true);
  }, []);
  const handleTagContract = useCallback(() => {
    if (isTagFromRetentionRef.current) {
      applyRetentionStep("contractTag");
    }
    window.open(
      "https://www.semparar.com.br/parceria-pedagio-eletronico",
      "_blank",
    );
  }, [applyRetentionStep]);
  const handleTagDismiss = useCallback(() => {
    if (isTagFromRetentionRef.current) {
      applyRetentionStep("dismissTag");
      return;
    }
    setShowTagBackdrop(false);
  }, [applyRetentionStep]);
  const handleAvulsoDeny = useCallback(
    () => applyRetentionStep("deny"),
    [applyRetentionStep],
  );
  const handleBeforeLeavePayNow = useCallback(
    () => applyRetentionStep("payNow"),
    [applyRetentionStep],
  );
  const handleBeforeLeaveDismiss = useCallback(
    () => applyRetentionStep("dismissBeforeLeave"),
    [applyRetentionStep],
  );

  return (
    <HomeView
      goToVehicles={goToVehicles}
      items={debts}
      plate={user?.plate || ""}
      isLoading={isLoading && !(skipBackdrops && debts.length > 0)}
      onChange={onChange}
      selectables={user?.cart || null}
      goBack={goBack}
      goToProfile={goToProfile}
      goGetout={goGetout}
      onSearchNewPlate={onSearchNewPlate}
      scrapersConsultation={
        shouldStartScrapers ? scrapersConsultation : undefined
      }
      isDefaultFlow={isDefaultFlow}
      isCaptchaVerified={isCaptchaVerified}
      isCaptchaMtcCaptcha={isCaptchaMtcCaptcha}
      captchaName={captchaName}
      resetCaptcha={resetCaptcha}
      incrementCaptchaKey={incrementCaptchaKey}
      isLoadingCaptchaFlag={isLoadingCaptchaFlag}
      showAvulsoBackdrop={showAvulsoBackdrop}
      avulsoPassageCount={debts.length}
      avulsoTotalValue={avulsoTotalValue}
      avulsoChannel={avulsoChannel}
      onAvulsoChannelChange={setAvulsoChannel}
      maskedPhone={maskPhone(user?.phone || "")}
      maskedEmail={maskEmail(user?.email || "")}
      showAvulsoWhatsApp={showAvulsoWhatsApp}
      showAvulsoEmail={showAvulsoEmail}
      onAvulsoConfirm={goToAvulsoPin}
      onAvulsoDeny={handleAvulsoDeny}
      isWebView={isWebView}
      showNoDataBackdrop={showNoDataBackdrop}
      onNoDataRegister={handleNoDataRegister}
      onNoDataDismiss={handleNoDataDismiss}
      showTagBackdrop={showTagBackdrop}
      onTagContract={handleTagContract}
      onTagDismiss={handleTagDismiss}
      showBeforeLeaveBackdrop={showBeforeLeaveBackdrop}
      onBeforeLeavePayNow={handleBeforeLeavePayNow}
      onBeforeLeaveDismiss={handleBeforeLeaveDismiss}
    />
  );
}
